import { useEffect, useState, useMemo } from 'react';
import { apiFetch } from '../utils/streamApi';
import PatrolFramePreview from '../components/PatrolFramePreview';
import PatrolLiveView from '../components/PatrolLiveView';

// Guard Check-in view — the guard opens this on their phone at the checkpoint and
// watches it flip to ✓ CHECKED IN the moment the engine registers it. Driven by the
// REAL /ui/patrol/status (no pose cost). Mobile-first, single column. Scoped to one
// route (picked from a dropdown when more than one exists).

const fmtClock = ts => { try { return new Date(ts * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); } catch { return '—'; } };
const fmtDur = sec => { const m = Math.max(0, Math.round(sec / 60)); return m < 60 ? `${m} min` : `${Math.floor(m / 60)}h ${m % 60}m`; };

const DONE = new Set(['COMPLETED', 'VERIFIED', 'VISITED_UNCONFIRMED', 'VISITED_DIFFERENT_PERSON']);
const CP_STATUS = {
  VERIFIED:                 { label: '✓ CHECKED IN',  bg: 'rgba(92,201,138,0.95)', fg: '#08130c' },
  COMPLETED:                { label: '✓ CHECKED IN',  bg: 'rgba(92,201,138,0.95)', fg: '#08130c' },
  VISITED_UNCONFIRMED:      { label: '✓ CHECKED IN',  bg: 'rgba(92,201,138,0.88)', fg: '#08130c' },
  VISITED_DIFFERENT_PERSON: { label: '✓ CHECKED IN',  bg: 'rgba(224,163,94,0.95)', fg: '#1a1000' },
  MISSED:                   { label: '✗ MISSED',      bg: 'rgba(224,112,112,0.95)', fg: '#1a0000' },
  PENDING:                  { label: '● WAITING…',    bg: 'rgba(70,70,70,0.7)',    fg: '#e8e8e8' },
};

export default function GuardCheckinPage() {
  const [status, setStatus] = useState(null);
  const [routeId, setRouteId] = useState('');
  const [adjust, setAdjust]   = useState(null);   // checkpoint id showing the detailed live view

  useEffect(() => {
    let alive = true;
    const poll = () => apiFetch('/ui/patrol/status').then(r => r.ok ? r.json() : null).then(d => { if (alive && d) setStatus(d); }).catch(() => {});
    poll();
    const t = setInterval(poll, 4000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const routes = useMemo(() => status?.routes || [], [status]);
  useEffect(() => { if (!routeId && routes.length) setRouteId(routes[0].id); }, [routes, routeId]);

  // Close the live-detail panel by itself once its checkpoint checks in.
  // PatrolLiveView is a stateless readout — it recomputes pose/zone/face on
  // every poll and has no knowledge of the engine's state — so it sat on
  // "WAITING…" even after the check-in was recorded, and the guard had to
  // dismiss it by hand. Declared above the early returns below to keep hook
  // order stable, so it re-derives the status rather than reusing liveByCp.
  useEffect(() => {
    if (!adjust) return;
    for (const r of routes) {
      for (const c of (r.active || [])[0]?.checkpoints || []) {
        if (c.id === adjust && DONE.has(c.status)) { setAdjust(null); return; }
      }
    }
  }, [adjust, routes]);

  if (!status)                  return <Msg>Loading…</Msg>;
  if (status.enabled === false) return <Msg>Guard Patrol is off.</Msg>;
  if (!routes.length)           return <Msg>No active patrol routes.</Msg>;

  const rt  = routes.find(r => r.id === routeId) || routes[0];
  const now = status.now, cur = rt.current_window, nxt = rt.next_window;
  const occ = (rt.active || [])[0];
  const liveByCp = {}; (occ?.checkpoints || []).forEach(c => { liveByCp[c.id] = c; });

  return (
    <div style={{ padding: '16px 14px', maxWidth: 560, margin: '0 auto', color: 'var(--text)' }}>
      <h1 style={{ fontSize: 22, margin: '0 0 10px' }}>🛡️ My Patrol</h1>

      {routes.length > 1 &&
        <select value={rt.id} onChange={e => setRouteId(e.target.value)} style={sel}>
          {routes.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>}

      <div style={{ ...card, textAlign: 'center' }}>
        {cur
          ? <><div style={{ color: '#5cc98a', fontWeight: 700, fontSize: 16 }}>Check-in window OPEN</div>
              <div style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 4 }}>{fmtClock(cur.window_start)}–{fmtClock(cur.window_end)} · closes in {fmtDur(cur.window_end - now)}</div></>
          : nxt
            ? <><div style={{ fontWeight: 700, fontSize: 16 }}>Next patrol {fmtClock(nxt.scheduled_time)}</div>
                <div style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 4 }}>in {fmtDur(nxt.window_start - now)} · window {fmtClock(nxt.window_start)}–{fmtClock(nxt.window_end)}</div></>
            : <span style={{ color: 'var(--text-muted)' }}>No upcoming patrol.</span>}
      </div>

      {rt.checkpoints.map(cp => {
        const st = liveByCp[cp.id]?.status;
        const s  = CP_STATUS[st] || (cur ? CP_STATUS.PENDING : null);
        const done = DONE.has(st);
        return (
          <div key={cp.id} style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
              <b style={{ fontSize: 18 }}>{cp.name}</b>
              {s && <span style={{ padding: '7px 16px', borderRadius: 22, fontWeight: 800, fontSize: 15, background: s.bg, color: s.fg }}>{s.label}</span>}
            </div>
            {adjust === cp.id
              ? <PatrolLiveView cameraIdx={cp.camera_idx} polygon={cp.patrol_zone || []}
                                expectedGuard={cp.expected_guard} holdSeconds={cp.gesture_hold_seconds || 2} />
              : <PatrolFramePreview cameraIdx={cp.camera_idx} polygon={cp.patrol_zone || []} highlight={done} />}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
              <span style={{ color: 'var(--text-muted)', fontSize: 13, flex: '1 1 200px' }}>
                {done ? (liveByCp[cp.id]?.identified_person ? `Verified: ${liveByCp[cp.id].identified_person}` : 'Checked in')
                      : cur ? 'Stand in the blue zone and face the camera (or raise a hand).'
                            : `Check-in opens ${nxt ? `at ${fmtClock(nxt.window_start)}` : 'per schedule'}.`}
              </span>
              <button onClick={() => setAdjust(a => a === cp.id ? null : cp.id)} style={adjustBtn}>
                {/* "Done" read as "mark me checked in" — it only ever closed
                    this panel, and the check-in is automatic. */}
                {adjust === cp.id ? '✕ Close' : '🔍 Adjust / live detail'}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Msg({ children }) { return <div style={{ padding: 24, color: 'var(--text-muted)', textAlign: 'center' }}>{children}</div>; }

const card = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 14, marginTop: 12 };
const sel  = { width: '100%', padding: '10px 12px', background: 'var(--surface-alt)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 16, marginBottom: 4 };
const adjustBtn = { padding: '8px 14px', background: 'var(--surface-alt)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer', fontSize: 14, whiteSpace: 'nowrap' };
